import React, { HTMLAttributes, useCallback, useEffect, useRef, useState } from "react"
import { ButtonPrimary, ButtonStatus, ButtonTertiary } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { InputOutlined } from "@nova/ds-component-input"

import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { BeforeYouSyncModalProps } from "../../sync/BeforeYouSyncModal"
import classNames from "classnames"
import { usePilotTrackingConnectedAccountMutation } from "../usePilotTrackingConnectedAccountMutation"
import { Status } from "@nova/ds-component-status"

export const ConnectedAccountCreateSkydioModal = ({
    confirm,
    cancel,
    isPerformingAsyncConfirmCallback: _,
    isPerformingAsyncCancelCallback: __,
    onCreate,
    ...rest
}: BeforeYouSyncModalProps & {
    onCreate: (token: string) => Promise<void>
} & HTMLAttributes<HTMLDivElement>) => {
    const [token, setToken] = useState("")
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        inputRef.current?.focus()
    }, [])

    const { trigger, isLoading } = usePilotTrackingConnectedAccountMutation({
        key: "skydio-connection",
        confirm,
        cancel,
        mutate: useCallback(async () => {
            await onCreate(token)
        }, [token, onCreate]),
        completionGlobalNotificationContext: {
            status: GlobalNotificationStatus.success,
            message: "Account Connected.",
            timeoutInMs: 3000,
        },
        failureGlobalNotificationContext: {
            status: GlobalNotificationStatus.error,
            message: "Unable to connect account. Check the API token",
            timeoutInMs: 5000,
        },
    })

    return (
        <HighPriorityNovaModalContainer
            {...rest}
            title="Connect Skydio Account"
            closeModal={cancel}
            className={classNames("w-[33.375rem]", rest.className)}
            bottomBar={
                <div className="flex items-center w-full">
                    <a
                        href="mailto:help@mapnova.com"
                        className="flex items-center gap-1.5 text-sm text-gray-300 hover:text-gray-0 transition-colors"
                    >
                        <Icons.SmallIcon Icon={Icons.Mail} iconClassName="text-gray-300" />
                        Get help
                    </a>
                    <div className="flex ml-auto gap-2">
                        <ButtonTertiary title="Cancel" onClick={cancel} />
                        <ButtonPrimary
                            title="Add Account"
                            onClick={() => trigger()}
                            status={isLoading ? ButtonStatus.Loading : ButtonStatus.Normal}
                            disabled={isLoading}
                        />
                    </div>
                </div>
            }
        >
            <div className="flex flex-col gap-5 mt-4">
                <p className="text-sm text-white">Set up live streaming for Skydio drones.</p>

                <div className="flex flex-col">
                    <div className="flex gap-3">
                        <div className="flex flex-col items-center">
                            <div className="flex items-center justify-center w-7 h-7 rounded-full border border-gray-500 text-gray-400 text-sm font-semibold shrink-0">
                                1
                            </div>
                            <div className="w-px flex-1 border-l border-gray-600" />
                        </div>
                        <div className="pb-5">
                            <p className="text-gray-0 font-semibold text-sm mb-2">
                                Enable live streaming permissions
                            </p>
                            <ol className="text-sm text-gray-300 list-decimal pl-4 space-y-1">
                                <li>
                                    Log in to{" "}
                                    <a
                                        href="https://cloud.skydio.com/entry/login"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-400 hover:text-blue-300 transition-colors"
                                    >
                                        Skydio Cloud.
                                    </a>
                                </li>
                                <li>{"Go to Settings > Live Streaming. > Live APIs"}</li>
                                <li>{"Enable RTSP Streaming & Live Telemetry."}</li>
                            </ol>
                        </div>
                    </div>

                    <div className="flex gap-3">
                        <div className="flex flex-col items-center">
                            <div className="flex items-center justify-center w-7 h-7 rounded-full border border-gray-500 text-gray-400 text-sm font-semibold shrink-0">
                                2
                            </div>
                        </div>
                        <div>
                            <p className="text-gray-0 font-semibold text-sm mb-2">
                                Generate an API token
                            </p>
                            <p className="text-sm text-gray-300 mb-2">
                                As an Organization Admin, configure permissions as follows:
                            </p>
                            <div className="flex gap-2 mb-2">
                                <Status type="neutral" variant="outline">
                                    Alerts - read &amp; write
                                </Status>
                                <Status type="neutral" variant="outline">
                                    Webhooks - read &amp; write
                                </Status>
                                <Status type="neutral" variant="outline">
                                    All others - read
                                </Status>
                            </div>
                            <a
                                href="https://support.skydio.com/hc/en-us/articles/4402958154651-How-to-generate-API-Tokens-in-Skydio-Cloud"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300 transition-colors"
                            >
                                How to generate an API token
                                <Icons.ExtraSmallIcon
                                    Icon={Icons.OpenInNew}
                                    iconClassName="text-blue-400 group-hover:text-blue-300"
                                />
                            </a>
                        </div>
                    </div>
                </div>

                <form
                    onSubmit={(e) => {
                        e.preventDefault()
                        trigger()
                    }}
                >
                    <InputOutlined
                        ref={inputRef}
                        placeholder="Paste your token here"
                        label="Skydio API Token*"
                        autoComplete="off"
                        type="text"
                        value={token}
                        onChange={(e) => setToken(e.target.value)}
                    />
                    <button type="submit" className="hidden" aria-hidden="true" />
                </form>
            </div>
        </HighPriorityNovaModalContainer>
    )
}
